import React from 'react';

import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';

import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';

import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import Dump from 'components/Dump';

import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useAppSelector } from 'hooks/redux';
import AccountSource from './reviewConditions/AccountSource';
import AmountSource from './reviewConditions/AmountSource';
import CategoryTarget from './reviewConditions/CategoryTarget';
import ExcludedTarget from './reviewConditions/ExcludedTarget';
import { SourceHeading, TargetHeading } from './reviewConditions/Headings';
import MemoTarget from './reviewConditions/MemoTarget';
import PayeeSource from './reviewConditions/PayeeSource';
import PayeeTarget from './reviewConditions/PayeeTarget';
import ReviewedTarget from './reviewConditions/ReviewedTarget';
import TagsTarget from './reviewConditions/TagsTarget';

const useStyles = makeStyles()((theme) => ({
  background: {
    background: theme.palette.background.default,
  },
  row: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    '> *': {
      lineHeight: '24px',
    },

    '>.MuiBox-root+.MuiBox-root': {
      padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
    },
    alignContent: 'flex-start',
    alignItems: 'center',
  },
  conditionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    '> div+div': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  column: {
    display: 'flex',
    flex: '0 0 50%',
    overflow: 'hidden',
    flexDirection: 'column',
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    gap: theme.spacing(1),
  },
  columnRow: {
    display: 'flex',
  },
  columnMain: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    gap: theme.spacing(1),
  },
  threeDotArea: {
    display: 'flex',
    flex: 0,
    alignSelf: 'flex-start',
  },
  rounded: {
    width: '100%',
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    '>*': {
      border: 'none',
    },
    '>:first-of-type': {
      borderTopRightRadius: 20,
      borderTopLeftRadius: 20,
    },
  },
}));

type RuleSummaryProps = {
  rule?: TransactionRuleType;
  onEditCallback?: (id: string) => void;
  onDeleteCallback?: (id: string) => void;
  onDuplicateCallback?: (id: string) => void;
  veritcalWithLabel?: boolean;
};

const RuleSummaryRow = ({
  rule,
  onEditCallback,
  onDeleteCallback,
  onDuplicateCallback,
  veritcalWithLabel,
}: RuleSummaryProps) => {
  const { classes, cx } = useStyles();
  const advanceRuleConditions = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'txnRulesAdvanced'),
  );

  const hasMenuActions = Boolean(rule && (onEditCallback || onDuplicateCallback || onDeleteCallback));

  return (
    <>
      {rule && (
        <Box
          className={cx(
            veritcalWithLabel ? (classes.column, classes.rounded) : (classes.row, classes.conditionsContainer),
          )}
        >
          <Dump obj={rule} />
          {veritcalWithLabel && <SourceHeading className={cx(veritcalWithLabel ? classes.background : '')} />}
          <Box className={classes.column}>
            <PayeeSource payee={rule.payeeSource} />
            {advanceRuleConditions && <AccountSource account={rule.accountSource} />}
            {advanceRuleConditions && <AmountSource amount={rule.amountSource} />}
          </Box>

          {veritcalWithLabel && <TargetHeading className={cx(veritcalWithLabel ? classes.background : '')} />}
          <Box className={classes.column}>
            <Box className={classes.columnRow}>
              <Box className={classes.columnMain}>
                <PayeeTarget payeeTarget={rule.payeeTarget} />
                <CategoryTarget
                  coaTarget={rule.coaTarget}
                  businessCoaTarget={rule.businessCoaTarget}
                  useDefaultAccount={!!rule.useDefaultAccountUsage}
                />
                {advanceRuleConditions && <TagsTarget tagTargetIds={rule.tagsTarget || []} />}
                {advanceRuleConditions && <MemoTarget memoTarget={rule.memoTarget} />}
                {advanceRuleConditions && <ExcludedTarget excludedTarget={rule.excludedTarget} />}
                {advanceRuleConditions && <ReviewedTarget reviewedTarget={rule.reviewedTarget} />}
              </Box>

              <Box className={classes.threeDotArea}>
                {hasMenuActions && (
                  <div role='none' onClick={(event) => event.stopPropagation()}>
                    <QDropdownMenu id='rules-list-three-dot-menu' aria-label='three-dot-menu' iconButtonSize='small'>
                      {onEditCallback ? (
                        <QMenuItem
                          variant='action'
                          label='Edit'
                          id='edit-rule'
                          startIcon={<EditIcon fontSize='small' />}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (rule.id) onEditCallback(rule.id);
                          }}
                        />
                      ) : null}
                      {onDuplicateCallback ? (
                        <QMenuItem
                          variant='action'
                          label='Duplicate'
                          id='duplicate-rule'
                          startIcon={<ControlPointDuplicateIcon fontSize='small' />}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (rule.id) onDuplicateCallback(rule.id);
                          }}
                        />
                      ) : null}
                      {onDeleteCallback && (onEditCallback || onDuplicateCallback) ? <QMenuDivider /> : null}
                      {onDeleteCallback ? (
                        <QMenuItem
                          variant='action'
                          label='Delete'
                          id='delete-rule'
                          destructive
                          startIcon={<DeleteIcon fontSize='small' />}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (rule.id) onDeleteCallback(rule.id);
                          }}
                        />
                      ) : null}
                    </QDropdownMenu>
                  </div>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default RuleSummaryRow;
